import { createSelector } from 'reselect';
import { RootReducer } from 'store/reducers';
import {
  COUNTABLE_STATS_CATEGORIES,
  DocumentCategory,
  Review,
  StatisticsByPage,
} from 'types/review-category';
import {
  PageGenericError,
  PageGenericErrorCode,
  ReviewCategoryError,
  ReviewCategoryErrorCode,
  ReviewCharacteristic,
  ReviewResult,
} from 'types/check';
import {
  AggregatedAssessmentPage,
  ChecksAlert,
  ChecksGroup,
} from 'types/check-results';
import { sortGroups } from 'utils/checks';
import { CHECK_DOT_SIZE, DOUBLE_BORDER_SIZE } from 'utils/borderBox';

export const currentPage = (state: RootReducer) =>
  state.assessmentPage.currentPage;

export const selectedCategory = (state: RootReducer) =>
  state.assessmentPage.selectedCategory;

export const scale = (state: RootReducer) => state.assessmentPage.scale;

export const selectedResult = (state: RootReducer) =>
  state.assessmentPage.selectedResult;

export const selectedCheck = (state: RootReducer) =>
  state.assessmentPage.selectedCheck;

export const externalId = (state: RootReducer) =>
  state.assessmentPage.externalId;

export const assessmentPageError = (state: RootReducer) =>
  state.assessmentPage.error;

export const getAlertsByCategory = createSelector(
  currentPage,
  selectedCategory,
  scale,
  (
    currentPage: AggregatedAssessmentPage | null,
    category: Review | DocumentCategory,
    scale: number
  ): ChecksAlert[] => {
    if (!currentPage || category === DocumentCategory.ssiCompleteness) {
      return [];
    }

    return currentPage[category].groups.map(alert => ({
      ...alert,
      borderBox: {
        width: alert.borderBox.width * scale,
        height: alert.borderBox.height * scale,
        left: alert.borderBox.left * scale,
        top: alert.borderBox.top * scale,
      },
    }));
  }
);

export const getAlertsByStatus = createSelector(
  getAlertsByCategory,
  selectedResult,
  (alerts, result): ChecksAlert[] =>
    alerts.filter(alert => {
      if (!result) return true;
      return alert.result === result;
    })
);

export const getGroupsByStatus = createSelector(
  getAlertsByStatus,
  (alerts): ChecksGroup[] =>
    sortGroups(
      alerts.reduce((result: ChecksGroup[], alert) => {
        const {
          borderBox: { top, height },
        } = alert;

        const isClaimsDependentContent = alert.checks.find(
          check =>
            check.characteristic === ReviewCharacteristic.claimsDependentContent
        );

        const alertHorizontalCenter = isClaimsDependentContent
          ? top
          : top + (height - CHECK_DOT_SIZE + DOUBLE_BORDER_SIZE) / 2;

        const groupIndex = result.findIndex(
          group => Math.abs(group.top - alertHorizontalCenter) <= CHECK_DOT_SIZE
        );

        if (groupIndex >= 0) {
          result[groupIndex].alerts.push(alert);
        } else {
          result.push({
            top: alertHorizontalCenter,
            alerts: [alert],
          });
        }

        return result;
      }, [])
    )
);

export const getCurrentAlert = createSelector(
  getAlertsByStatus,
  selectedCheck,
  (alerts, id): ChecksAlert | undefined => alerts.find(alert => alert.id === id)
);

export const getAlertByExternalId = createSelector(
  getAlertsByStatus,
  externalId,
  (alerts, externalId): ChecksAlert | undefined =>
    alerts.find(alert => alert.externalId === externalId)
);

export const getAlertsByParentGroup = createSelector(
  getAlertsByStatus,
  getCurrentAlert,
  (alerts, currentAlert): string[] => {
    if (!alerts || !currentAlert || !currentAlert.number) return [];

    return alerts.reduce((result, alert) => {
      if (alert.number?.groupNumber === currentAlert.number?.number) {
        alert.number && result.push(alert.number?.number);
      }

      return result;
    }, [] as string[]);
  }
);

export const getCurrentGroupIndex = createSelector(
  getGroupsByStatus,
  selectedCheck,
  (groups, id): number => {
    let groupIndex = -1;

    groups.forEach((group, index) => {
      const groupAlertIndex = group.alerts.findIndex(alert => alert.id === id);

      if (groupAlertIndex >= 0) {
        groupIndex = index;
      }
    });

    return groupIndex;
  }
);

export const getStatisticsByPage = createSelector(
  currentPage,
  (currentPage): StatisticsByPage => {
    const getAlertsLengthByStatus = (
      alerts: ChecksAlert[],
      result?: ReviewResult
    ) =>
      alerts.filter(check => {
        if (!result) {
          return true;
        }

        return check.result === result;
      }).length;

    const result = {} as StatisticsByPage;

    COUNTABLE_STATS_CATEGORIES.forEach(reviewCategory => {
      const alerts = currentPage ? currentPage[reviewCategory].groups : [];

      result[reviewCategory] = {
        all: currentPage ? getAlertsLengthByStatus(alerts) : 0,
        [ReviewResult.pass]: currentPage
          ? getAlertsLengthByStatus(alerts, ReviewResult.pass)
          : 0,
        [ReviewResult.fail]: currentPage
          ? getAlertsLengthByStatus(alerts, ReviewResult.fail)
          : 0,
        [ReviewResult.warning]: currentPage
          ? getAlertsLengthByStatus(alerts, ReviewResult.warning)
          : 0,
      };
    });

    return result;
  }
);

export const getErrorsByCategory = createSelector(
  currentPage,
  selectedCategory,
  (currentPage, selectedCategory): ReviewCategoryError[] => {
    if (!currentPage) {
      return [];
    }

    if (!currentPage[selectedCategory]) {
      return [];
    }

    if (!currentPage[selectedCategory].errors) {
      currentPage[selectedCategory].errors = [];
    }

    return currentPage[selectedCategory].errors;
  }
);

export const getPageErrors = createSelector(
  currentPage,
  (currentPage): PageGenericError[] => {
    if (!currentPage?.errors) return [];

    return currentPage.errors.filter(
      error => error.code !== PageGenericErrorCode.PAGE_IS_IMAGE
    );
  }
);
export const getErrorsByPage = createSelector(
  currentPage,
  (currentPage): ReviewCategoryError[] => {
    if (!currentPage) return [];

    return Object.values(Review).reduce(
      (errors: ReviewCategoryError[], category: Review) =>
        errors.concat(currentPage[category].errors),
      []
    );
  }
);

export const getReviewCategoryAvailability = createSelector(
  getErrorsByPage,
  (_: any, blockers: ReviewCategoryErrorCode[]) => blockers,
  (errors, blockers): boolean =>
    errors.filter(err => Object.values(blockers).includes(err.code)).length ===
    0
);
